/**
 * APIKeyManager Component
 * 
 * API key management interface
 * 
 * Version: 6.2.0
 */
import React, { useState } from 'react'
import './APIKeyManager.css'

interface APIKey {
  id: string
  name: string
  key: string
  secret: string
  broker: string
  permissions: string[]
  createdAt: Date
  lastUsed?: Date
  active: boolean
}

interface APIKeyManagerProps {
  keys?: APIKey[]
  onKeyCreate?: (key: Omit<APIKey, 'id' | 'createdAt'>) => void
  onKeyRevoke?: (id: string) => void
  onKeyToggle?: (id: string, active: boolean) => void
  className?: string
}

// Default keys
const defaultKeys: APIKey[] = [
  { 
    id: '1', 
    name: 'Zerodha Production', 
    key: 'zk_test_xxxxxxxxxxxx', 
    secret: '••••••••••••••••',
    broker: 'Zerodha',
    permissions: ['READ', 'TRADE', 'FUNDS'],
    createdAt: new Date('2026-01-15'),
    lastUsed: new Date('2026-06-12'),
    active: true
  },
  { 
    id: '2', 
    name: 'Angel One Demo', 
    key: 'ag_test_yyyyyyyyyyyy', 
    secret: '••••••••••••••••',
    broker: 'Angel One',
    permissions: ['READ', 'TRADE'],
    createdAt: new Date('2026-03-20'),
    active: false
  },
]

const APIKeyManager: React.FC<APIKeyManagerProps> = ({
  keys = defaultKeys,
  onKeyCreate,
  onKeyRevoke,
  onKeyToggle,
  className = ''
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [revealedSecrets, setRevealedSecrets] = useState<Set<string>>(new Set())
  const [newKey, setNewKey] = useState({
    name: '',
    key: '',
    secret: '',
    broker: '',
    permissions: [] as string[]
  })

  const brokers = ['Zerodha', 'Angel One', 'ICICI Direct', 'HDFC Securities', 'Upstox', '5Paisa', 'AliceBlue']
  const availablePermissions = ['READ', 'TRADE', 'FUNDS', 'PROFILE', 'ORDERS', 'POSITIONS', 'HOLDINGS']

  const toggleSecretReveal = (id: string) => {
    setRevealedSecrets(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleCreate = () => {
    if (!newKey.name || !newKey.key || !newKey.broker) return

    onKeyCreate?.({
      name: newKey.name,
      key: newKey.key,
      secret: newKey.secret,
      broker: newKey.broker,
      permissions: newKey.permissions,
      active: true
    })

    setNewKey({ name: '', key: '', secret: '', broker: '', permissions: [] })
    setShowCreateForm(false)
  }

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  return (
    <div className={`api-key-manager ${className}`}>
      {/* Header */}
      <div className="key-manager-header">
        <div className="header-title">
          <h3>🔑 API Key Manager</h3>
          <span className="key-count">{keys.length} keys</span>
        </div>
        <button 
          className="add-key-btn"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : '+ Add API Key'}
        </button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="create-key-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Key Name</label>
              <input
                type="text"
                placeholder="My Trading API"
                value={newKey.name}
                onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Broker</label>
              <select
                value={newKey.broker}
                onChange={(e) => setNewKey({ ...newKey, broker: e.target.value })}
                className="form-select"
              >
                <option value="">Select Broker</option>
                {brokers.map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>API Key</label>
              <input
                type="text"
                placeholder="Enter API key"
                value={newKey.key}
                onChange={(e) => setNewKey({ ...newKey, key: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>API Secret</label>
              <input
                type="password"
                placeholder="Enter API secret"
                value={newKey.secret}
                onChange={(e) => setNewKey({ ...newKey, secret: e.target.value })}
                className="form-input"
              />
            </div>
          </div>
          <div className="form-group full-width">
            <label>Permissions</label>
            <div className="permissions-grid">
              {availablePermissions.map(perm => (
                <label key={perm} className="permission-checkbox">
                  <input
                    type="checkbox"
                    checked={newKey.permissions.includes(perm)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewKey({ ...newKey, permissions: [...newKey.permissions, perm] })
                      } else {
                        setNewKey({ ...newKey, permissions: newKey.permissions.filter(p => p !== perm) })
                      }
                    }}
                  />
                  <span>{perm}</span>
                </label>
              ))}
            </div>
          </div>
          <button 
            className="submit-btn"
            onClick={handleCreate}
            disabled={!newKey.name || !newKey.key || !newKey.broker}
          >
            Add API Key
          </button>
        </div>
      )}

      {/* Keys List */}
      <div className="keys-list">
        {keys.map(apiKey => (
          <div 
            key={apiKey.id}
            className={`key-item ${!apiKey.active ? 'inactive' : ''}`}
          >
            <div className="key-main">
              <div className="key-header">
                <span className="key-name">{apiKey.name}</span>
                <span className="key-broker">{apiKey.broker}</span>
              </div>
              <div className="key-credentials">
                <div className="credential-row">
                  <span className="cred-label">Key:</span>
                  <code className="cred-value">{apiKey.key}</code>
                  <button 
                    className="copy-btn"
                    onClick={() => copyToClipboard(apiKey.key)}
                    title="Copy"
                  >
                    📋
                  </button>
                </div>
                <div className="credential-row">
                  <span className="cred-label">Secret:</span>
                  <code className="cred-value">
                    {revealedSecrets.has(apiKey.id) ? apiKey.secret : '••••••••••••••••'}
                  </code>
                  <button 
                    className="reveal-btn"
                    onClick={() => toggleSecretReveal(apiKey.id)}
                    title={revealedSecrets.has(apiKey.id) ? 'Hide' : 'Reveal'}
                  >
                    {revealedSecrets.has(apiKey.id) ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              <div className="key-permissions">
                {apiKey.permissions.map(perm => (
                  <span key={perm} className="permission-tag">{perm}</span>
                ))}
              </div>
            </div>
            
            <div className="key-meta">
              <div className="meta-row">
                <span>Created:</span>
                <span>{formatDate(apiKey.createdAt)}</span>
              </div>
              {apiKey.lastUsed && (
                <div className="meta-row">
                  <span>Last Used:</span>
                  <span>{formatDate(apiKey.lastUsed)}</span>
                </div>
              )}
            </div>
            
            <div className="key-actions">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={apiKey.active}
                  onChange={(e) => onKeyToggle?.(apiKey.id, e.target.checked)}
                />
                <span className="toggle-slider" />
              </label>
              <button 
                className="revoke-btn"
                onClick={() => onKeyRevoke?.(apiKey.id)}
                title="Revoke Key"
              >
                🚫
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default APIKeyManager